import { useLocation, Link } from 'react-router-dom';
import { Mail, MapPin, Check, ArrowRight, Shield, Wrench, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';

const localPages = {
  '/spa-nantes': {
    title: 'Vente & Installation de Spa à Nantes',
    subtitle: 'Votre expert spa en Loire-Atlantique (44)',
    location: 'Nantes',
    department: 'Loire-Atlantique (44)',
    description: 'Atlantique Spa Boutique, votre spécialiste de la vente et installation de spas à Nantes et en Loire-Atlantique. Technicien expert, SAV local, accompagnement durable.',
    areas: ['Nantes', 'Saint-Nazaire', 'Saint-Herblain', 'Rezé', 'Orvault', 'La Chapelle-sur-Erdre', 'Vertou', 'Carquefou']
  },
  '/spa-vendee': {
    title: 'Vente & Installation de Spa en Vendée',
    subtitle: 'Votre expert spa dans le 85',
    location: 'Vendée',
    department: 'Vendée (85)',
    description: 'Atlantique Spa Boutique intervient dans toute la Vendée pour la vente, l\'installation et le SAV de spas. Expertise technique garantie.',
    areas: ['La Roche-sur-Yon', 'Les Sables-d\'Olonne', 'Challans', 'Saint-Gilles-Croix-de-Vie', 'Fontenay-le-Comte', 'Luçon']
  },
  '/spa-morbihan': {
    title: 'Vente & Installation de Spa dans le Morbihan',
    subtitle: 'Votre expert spa dans le 56',
    location: 'Morbihan',
    department: 'Morbihan (56)',
    description: 'Atlantique Spa Boutique, spécialiste spa dans le Morbihan. Vente, installation et SAV par un technicien expert.',
    areas: ['Vannes', 'Lorient', 'Lanester', 'Ploemeur', 'Hennebont', 'Auray', 'Pontivy']
  },
  '/spa-maine-et-loire': {
    title: 'Vente & Installation de Spa en Maine-et-Loire',
    subtitle: 'Votre expert spa dans le 49',
    location: 'Maine-et-Loire',
    department: 'Maine-et-Loire (49)',
    description: 'Atlantique Spa Boutique intervient en Maine-et-Loire pour la vente et l\'installation de spas. SAV local garanti.',
    areas: ['Angers', 'Cholet', 'Saumur', 'Avrillé', 'Les Ponts-de-Cé', 'Trélazé', 'Saint-Barthélemy-d\'Anjou']
  }
};

const LocalSeoPage = () => {
  const location = useLocation();
  const pageData = localPages[location.pathname] || localPages['/spa-nantes'];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="local-seo-page">
      <SEO 
        title={pageData.title}
        description={pageData.description}
        canonical={location.pathname}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Spa {pageData.location}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero - Full Width Image */}
      <FullWidthHero
        variant="page"
        image={realPhotos.installation1}
        imageAlt={`Installation spa ${pageData.location} - ${realPhotosAlt.installation1}`}
        badge={pageData.department}
        badgeIcon={MapPin}
        title={pageData.title}
        subtitle={pageData.description}
        primaryCta={{ text: "Demander un Devis Gratuit", href: "/devis", icon: ArrowRight }}
        overlayPosition="left"
      />

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Nos services en {pageData.location}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: "Vente de spas", desc: "Large gamme Origin Spa" },
              { icon: Wrench, title: "Installation expert", desc: "Sur devis personnalisé" },
              { icon: Shield, title: "SAV local", desc: "Intervention rapide" },
              { icon: Clock, title: "Entretien", desc: "Maintenance régulière" }
            ].map((service, i) => (
              <Card key={i} className="border-slate-200 text-center card-premium">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-7 h-7 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{service.title}</h3>
                  <p className="text-sm text-slate-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Pourquoi choisir Atlantique Spa Boutique en {pageData.location} ?
              </h2>
              <ul className="space-y-4">
                {[
                  "Technicien spécialiste, pas simple revendeur",
                  "SAV local avec intervention rapide",
                  "Installation soignée par nos équipes",
                  "Stock de pièces détachées disponible",
                  "Conseils d'expert personnalisés",
                  "Accompagnement durable après vente"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">Zones desservies</h3>
                  <div className="flex flex-wrap gap-2">
                    {pageData.areas.map((area) => (
                      <span key={area} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                        {area}
                      </span>
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm mt-4">
                    Et toutes les communes du {pageData.department}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Un projet de spa en {pageData.location} ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contactez-nous pour un devis gratuit et un conseil personnalisé
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 btn-lift px-8">
                Demander un Devis
              </Button>
            </Link>
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-slate-800 gap-2 px-8">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Other locations */}
      <section className="py-12 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-600 mb-4">Nous intervenons également dans :</p>
          <div className="flex flex-wrap gap-3 justify-center">
            {Object.entries(localPages)
              .filter(([path]) => path !== location.pathname)
              .map(([path, data]) => (
                <Link key={path} to={path}>
                  <Button variant="outline" size="sm">
                    Spa {data.location}
                  </Button>
                </Link>
              ))
            }
          </div>
        </div>
      </section>

      {/* Schema.org LocalBusiness */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": `Atlantique Spa Boutique - ${pageData.location}`,
          "description": pageData.description,
          
          "email": "contact@atlantiquespaboutique.com",
          "areaServed": pageData.areas,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": pageData.location,
            "addressRegion": pageData.department,
            "addressCountry": "FR"
          },
          "openingHours": "Mo-Sa 09:00-19:00"
        })
      }} />
    </div>
  );
};

export default LocalSeoPage;
